import * as React from 'react';
import { Editor } from 'druide-slate-react';
import { Value, Block, Mark } from 'slate';
import { OrderedSet } from 'immutable';
import isEqual from 'lodash.isequal';
import { Dispatch } from 'react-redux';

import { LIST_TYPE, STYLE_TYPES, TEXT_ALIGN, BLOCK_TYPES } from './helpers/constants';
import helpers from './helpers';
import { MarkProperties } from './helpers/marks';
import { StyledEditorWrapper } from './styles';
import { StoreState } from '../../../reducers';
import { getBorderWidth, LinkData, setVerticalAlignClassName } from '../../../containers/Editor/utils/helpers';
import { updateEditableTextField, setActiveItem, toggleEditPanel } from '../../../containers/Editor/actions';
import EditPanel from '../../../containers/Editor/EditPanel';
import TextPanel from '../../../containers/Editor/EditPanel/TextPanel';
import LinkPanel from '../../../containers/Editor/EditPanel/LinkPanel';
import ThemesPanel from '../../../containers/Editor/EditPanel/ThemesPanel';
import { EditorType, EditableType, IGNORE_PAGE_CLICK_CLASSNAME, VerticalAlignClassName } from '../../../containers/Editor/constants';
import Utils from '../../../utils';

interface TextAlignFormat {
	textAlign: string;
	textAlignLast: string;
}

interface LineHeightFormat {
	lineHeight: string;
	minHeight: string;
}

interface RichTextEditorProps {
	editorType: EditorType;
	page: number;
	id: string;
	width?: number | string;
	height?: number | string;
	pageScale?: number;
	selfScale?: number;
	transformStyle?: string;
	content: string;
	isActive: boolean;
	parentId: string | null;
	activeParentId: string | null;
	isEditPanelDisplayed: boolean;
	shouldShowScrollbar: boolean;
	verticalAlignClassName: VerticalAlignClassName;
	spreads: number[][];
	isThemedItem: boolean;
	dispatch: Dispatch<StoreState>;
	isAccessibleVersion: boolean;
}

interface RichTextEditorState {
	value: Value;
}

class RichTextEditor extends React.Component<RichTextEditorProps, RichTextEditorState> {
	private editor: Editor;
	private currentMarks: OrderedSet<Mark>;
	private textPanelActions;

	// tslint:disable-next-line: max-line-length
	// This variable is used for defining which set of marks (value.activeMarks or value.marks) to select for correct display of format icons (font-weight, font-style, underline)
	// in Edit panel. When format is changed and new mark is added (in handleMarkChange) value.activeMarks should be selected because value.marks keeps previous mark (Slate issue?)
	// which leads to incorrect format icon highlighting (e.g., when change a font-weight from normal to bold value.marks contains both normal and bold marks).
	// When some text is selected without any format change value.marks should be selected because value.activeMarks doesn't contain
	// the same marks from different parts of value.selection (Slate issue?) (e.g., when there are 2 parts of selection with bold mark value.activeMarks doesn't contain
	// bold mark which is incorrect).
	private isSomeMarkChanged: boolean;

	static isEmpty(value: Value) {
		return !value.document.text.trim();
	}

	constructor(props: RichTextEditorProps) {
		super(props);

		const DEFAULT_CONTENT = '<p></p>';

		this.state = {
			value: helpers.html.deserialize(props.content || DEFAULT_CONTENT),
		};

		this.currentMarks = this.state.value.marks;

		this.textPanelActions = {
			onFontFamilyChange: this.handleMarkChange.bind(this, STYLE_TYPES.FONT_FAMILY),
			onFontSizeChange: this.handleMarkChange.bind(this, STYLE_TYPES.FONT_SIZE),
			onColorChange: this.handleMarkChange.bind(this, STYLE_TYPES.COLOR),
			onLineHeightChange: this.handleLineHeightChange.bind(this),
			onLetterSpacingChange: this.handleMarkChange.bind(this, STYLE_TYPES.LETTER_SPACING),
			onFontWeightClick: this.handleMarkChange.bind(this, STYLE_TYPES.FONT_WEIGHT),
			onFontStyleClick: this.handleMarkChange.bind(this, STYLE_TYPES.FONT_STYLE),
			onTextDecorationChange: this.handleMarkChange.bind(this, STYLE_TYPES.TEXT_DECORATION),
			onTextTransformChange: this.handleMarkChange.bind(this, STYLE_TYPES.TEXT_TRANSFORM),
			onTextScriptChange: this.handleMarkChange.bind(this, STYLE_TYPES.TEXT_SCRIPT),
			onTextAlignChange: this.handleTextAlignChange.bind(this),
			onVerticalAlignChange: this.handleVerticalAlignChange.bind(this),
			onListTypeChange: this.handleListTypeChange.bind(this),
		};

		this.handleChange = this.handleChange.bind(this);
		this.handleMouseDown = this.handleMouseDown.bind(this);
		this.handleLinkChange = this.handleLinkChange.bind(this);
	}

	shouldComponentUpdate(nextProps: RichTextEditorProps) {
		const { isActive, shouldShowScrollbar, content, verticalAlignClassName } = this.props;
		return isActive ||
			isActive !== nextProps.isActive ||
			shouldShowScrollbar !== nextProps.shouldShowScrollbar ||
			content !== nextProps.content ||
			verticalAlignClassName !== nextProps.verticalAlignClassName;
	}

	componentDidUpdate(prevProps: RichTextEditorProps) {
		const {
			isActive,
			id,
			parentId,
			activeParentId,
			isEditPanelDisplayed,
			content,
			verticalAlignClassName,
		} = this.props;

		if (content !== prevProps.content) {
			this.setState({
				value: helpers.html.deserialize(content),
			}, () => {
				// is needed to re-render when content from props was changed (otherwise there won't be a visual change because of shouldComponentUpdate)
				this.forceUpdate();
			});
		}

		if (verticalAlignClassName !== prevProps.verticalAlignClassName) {
			const container = document.querySelector(`#${id}`) as HTMLElement;
			setVerticalAlignClassName(container, verticalAlignClassName);
		}

		if (!prevProps.isEditPanelDisplayed && isEditPanelDisplayed) {
			this.hideTouchKeyboard();
		}

		const isBecomeActive = !prevProps.isActive && isActive;
		const isBecomeInActive = prevProps.isActive && !isActive;

		if (isBecomeInActive) {
			this.hideTouchKeyboard();
			this.handleBlur();
		}

		if (parentId) {
			const isParentIdChanged = prevProps.activeParentId !== activeParentId;
			const parentContainer = document.querySelector(`#${parentId}`);

			if (parentContainer) {
				if (isBecomeActive) {
					parentContainer.classList.add('is-visible');
				}

				if (isBecomeInActive && isParentIdChanged) {
					parentContainer.classList.remove('is-visible');
				}
			}
		}
	}

	hideTouchKeyboard() {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
	}

	handleChange({ value }: { value: Value }) {
		const { isActive, editorType } = this.props;

		if (!isActive) {
			return;
		}

		if (editorType === EditorType.INDESIGN) {
			const shouldUpdateCurrentMarks = value.marks.size && !isEqual(this.currentMarks, value.marks);
			if (shouldUpdateCurrentMarks) {
				this.currentMarks = value.marks;
			}

			this.setState({ value }, () => {
				// By default Slate removes all marks when there is no text in paragraph. We need to keep previous marks and apply them for proper text format
				const shouldCorrectMarks = !value.marks.size && !!this.currentMarks.size;
				if (shouldCorrectMarks) {
					this.editor.withoutSaving(() => {
						this.currentMarks.forEach((mark: Mark) => this.editor.addMark(mark));
					});
				}
			});
		} else {
			this.setState({ value });
		}
	}

	handleBlur() {
		const { content, page, id, dispatch, isAccessibleVersion } = this.props;
		const { value } = this.state;
		const serializedContent = helpers.html.serialize(value) ? helpers.html.serialize(value) : '<p></p>';
		const isContentChanged = serializedContent !== content;

		if (isContentChanged) {
			dispatch(updateEditableTextField(
				page,
				id,
				{
					content: serializedContent,
					isEmpty: RichTextEditor.isEmpty(value),
				},
				true,
				isAccessibleVersion,
			));
		}
	}

	handleMouseDown() {
		const { id, page, parentId, isActive, isEditPanelDisplayed, dispatch } = this.props;
		if (!isActive) {
			dispatch(setActiveItem(id, page, parentId));

			if (!isEditPanelDisplayed) {
				dispatch(toggleEditPanel());
			}
		}
	}

	handleMarkChange(type: STYLE_TYPES, param: string) {
		const { value: { marks, activeMarks, fragment } } = this.state;
		let newMark: MarkProperties;
		const correctMarks = this.isSomeMarkChanged ? activeMarks : marks;

		switch (type) {
			case STYLE_TYPES.FONT_FAMILY:
				newMark = helpers.getFontFamilyMark(param);
				break;
			case STYLE_TYPES.FONT_SIZE:
				newMark = helpers.getFontSizeMark(param);
				break;
			case STYLE_TYPES.COLOR:
				newMark = helpers.getColorMark(param);
				break;
			case STYLE_TYPES.FONT_STYLE:
				newMark = helpers.getFontStyleMark(correctMarks);
				break;
			case STYLE_TYPES.FONT_WEIGHT:
				newMark = helpers.getFontWeightMark(correctMarks);
				break;
			case STYLE_TYPES.TEXT_DECORATION:
				newMark = helpers.getTextDecorationMark(correctMarks, param);
				break;
			case STYLE_TYPES.TEXT_TRANSFORM:
				newMark = helpers.getTextTransformMark(correctMarks, param);
				break;
			case STYLE_TYPES.TEXT_SCRIPT:
				newMark = helpers.getTextScriptMark(correctMarks, param);
				break;
			case STYLE_TYPES.LETTER_SPACING:
				newMark = helpers.getLetterSpacingMark(param);
				break;
			default:
				return;
		}

		helpers.getMarksByType(marks, type).map((mark: Mark) => this.editor.removeMark(mark));
		this.editor.addMark(newMark);
		this.isSomeMarkChanged = true;
		this.editor.focus();

		const isListInFragment = fragment.nodes.some(node => (
			!!node &&
			node.object === 'block' &&
			[BLOCK_TYPES.ul, BLOCK_TYPES.ol].includes(node.type as BLOCK_TYPES)
		));

		if ([
			STYLE_TYPES.FONT_FAMILY,
			STYLE_TYPES.FONT_SIZE,
			STYLE_TYPES.COLOR,
			STYLE_TYPES.FONT_STYLE,
			STYLE_TYPES.FONT_WEIGHT,
		].includes(type) && isListInFragment) {
			helpers.updateListItemsStyles(this.editor, newMark.data.style);
		}

		setTimeout(() => {
			this.isSomeMarkChanged = false;
			this.hideTouchKeyboard();
		}, 0);
	}

	handleBlockFormatChange(data: TextAlignFormat | LineHeightFormat) {
		const { value: { blocks } } = this.state;

		if (blocks) {
			blocks.forEach(({ key, type, data: currentData }: Block) => {
				const newData = {
					style: {
						...currentData.get('style'),
						...data,
					},
				};
				this.editor.setNodeByKey(key, {
					type,
					data: newData,
				});
			});
		}

		setTimeout(() => {
			this.hideTouchKeyboard();
		}, 0);
	}

	handleTextAlignChange(textAlign: TEXT_ALIGN) {
		this.handleBlockFormatChange({
			textAlign,
			textAlignLast: 'auto',
		});
	}

	handleLineHeightChange(lineHeight: string) {
		this.handleBlockFormatChange({
			lineHeight,
			minHeight: lineHeight,
		});
	}

	handleVerticalAlignChange(newVerticalAlignClassName: VerticalAlignClassName) {
		const { page, id, verticalAlignClassName, dispatch } = this.props;

		if (verticalAlignClassName !== newVerticalAlignClassName) {
			dispatch(updateEditableTextField(page, id, {
				verticalAlignClassName: newVerticalAlignClassName,
			}));

			const container = document.querySelector(`#${id}`) as HTMLElement;
			setVerticalAlignClassName(container, newVerticalAlignClassName);
		}
	}

	handleListTypeChange(listType: LIST_TYPE) {
		const { value, value: { blocks } } = this.state;

		if (listType === LIST_TYPE.NONE) {
			helpers.removeList(this.editor);
			// remove unneeded styles in case previously it was a list item
			helpers.removeListItemsStyles(this.editor);
		} else {
			const isList = blocks.some(block => !!block && block.type === BLOCK_TYPES.li);

			if (isList) {
				const currentListType = helpers.getListType(value);

				if (currentListType !== listType) {
					helpers.updateList(this.editor, listType);
					helpers.addListItemsStyles(this.editor);
				}
			} else {
				helpers.addList(this.editor, listType);
				helpers.addListItemsStyles(this.editor);
			}
		}

		this.editor.focus();

		setTimeout(() => {
			this.hideTouchKeyboard();
		}, 0);
	}

	handleLinkChange(linkData: LinkData) {
		const { value } = this.state;
		helpers.updateLink(this.editor, value, linkData);
	}

	renderEditPanel() {
		const { isActive, isEditPanelDisplayed, page, verticalAlignClassName, spreads, isThemedItem } = this.props;
		const { value } = this.state;
		const { blocks, inlines, selection, marks, activeMarks } = value;
		const correctMarks = this.isSomeMarkChanged ? activeMarks : marks;

		const panels = {
			[EditableType.text]: (
				<TextPanel
					page={page}
					fontFamily={helpers.getFontFamily(marks)}
					fontSize={helpers.getFontSize(marks)}
					color={helpers.getColor(marks)}
					lineHeight={helpers.getLineHeight(blocks)}
					letterSpacing={helpers.getLetterSpacing(marks)}
					isBoldActive={helpers.isFontWeightBold(correctMarks)}
					isItalicActive={helpers.isFontStyleItalic(correctMarks)}
					isUnderlineActive={helpers.isTextDecorationUnderline(correctMarks)}
					isLineThroughActive={helpers.isTextDecorationLineThrough(correctMarks)}
					isCapitalizeActive={helpers.isTextTransformCapitalize(correctMarks)}
					isUpperCaseActive={helpers.isTextTransformUpperCase(correctMarks)}
					isLowerCaseActive={helpers.isTextTransformLowerCase(correctMarks)}
					isSuperScriptActive={helpers.isSuperScript(correctMarks)}
					isSubScriptActive={helpers.isSubScript(correctMarks)}
					isTextAlignLeft={helpers.isTextAlignLeft(blocks)}
					isTextAlignCenter={helpers.isTextAlignCenter(blocks)}
					isTextAlignRight={helpers.isTextAlignRight(blocks)}
					isTextAlignJustify={helpers.isTextAlignJustify(blocks)}
					isVerticalAlignTop={!verticalAlignClassName}
					isVerticalAlignCenter={verticalAlignClassName === VerticalAlignClassName.CENTER}
					isVerticalAlignBottom={verticalAlignClassName === VerticalAlignClassName.BOTTOM}
					listType={helpers.getListType(value)}
					hasColorPicker={!isThemedItem}
					actions={this.textPanelActions}
				/>
			),
			[EditableType.link]: (
				<LinkPanel
					linkData={helpers.getLinkData(inlines, spreads)}
					canAddLink={selection.isExpanded}
					onChange={this.handleLinkChange}
				/>
			),
		};

		if (isThemedItem) {
			panels[EditableType.themes] = <ThemesPanel />;
		}

		return isActive && isEditPanelDisplayed && (
			<EditPanel
				defaultType={EditableType.text}
				panels={panels}
			/>
		);
	}

	render() {
		const {
			editorType,
			isActive,
			width,
			height,
			transformStyle,
			pageScale = 1,
			selfScale = 1,
			shouldShowScrollbar,
			isAccessibleVersion,
		} = this.props;
		const { value } = this.state;

		// is needed for rendering in iframe (for html editor)
		// width is set on parent container to avoid incorrect width calculation in case it is set in %
		const style = {
			height,
		};

		const editorClasses = Utils.getCssClasses(`${IGNORE_PAGE_CLICK_CLASSNAME} uprez-rte-wrapper`, {
			'is-active': isActive,
		});

		const isEmpty = RichTextEditor.isEmpty(value);

		return (
			<StyledEditorWrapper
				className={editorClasses}
				editorType={editorType}
				isActive={isActive}
				isEmpty={isEmpty}
				width={width}
				height={height}
				transform={transformStyle}
				outlineWidth={getBorderWidth(pageScale * selfScale)}
				style={editorType === EditorType.HTML ? style : undefined}
				shouldShowScrollbar={shouldShowScrollbar && !isEmpty}
				isAccessibleVersion={isAccessibleVersion}
			>
				<Editor
					ref={(editor: Editor) => { this.editor = editor; }}
					className="uprez-rte"
					value={value}
					onMouseDown={this.handleMouseDown}
					onTouchStart={this.handleMouseDown}
					onChange={this.handleChange}
					onBeforeInput={helpers.handleBeforeInput}
					onCopy={helpers.handleCopy}
					onCut={helpers.handleCopy}
					onPaste={helpers.handlePaste}
					onKeyDown={helpers.handleKeyDown}
					renderBlock={helpers.renderBlock}
					renderInline={helpers.renderInline}
					renderMark={helpers.renderMark}
				/>
				{this.renderEditPanel()}
			</StyledEditorWrapper>
		);
	}
}

export default RichTextEditor;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/RichTextEditor/index.tsx